import { MODULE_ENTRY_WHOLE_MODULE_KEY } from './constants';
import { isEntryLocked } from './entry-policy';
import type {
  GovernedModule,
  ListConfigKey,
  ListEntry,
  MergeModuleConfigOptions,
} from './types';

export function getModuleListConfigKey(type: string): ListConfigKey | null {
  switch (type) {
    case 'socials':
      return 'links';
    case 'contact_info':
    case 'links':
      return 'items';
    default:
      return null;
  }
}

/** Merges template list entries with profile/client overrides per entry policy. */
export function mergeListModuleConfig(
  templateModule: GovernedModule,
  sourceModule: GovernedModule,
  listKey: ListConfigKey,
  options: MergeModuleConfigOptions,
): Record<string, unknown> {
  const { resyncOnly, publishRollout } = options;
  const templateConfig = (templateModule.config ?? {}) as Record<
    string,
    unknown
  >;
  const sourceConfig = (sourceModule.config ?? {}) as Record<string, unknown>;
  const templateEntries = (templateConfig[listKey] as ListEntry[]) ?? [];
  const sourceEntries = (sourceConfig[listKey] as ListEntry[]) ?? [];
  const sourceById = new Map(
    sourceEntries
      .filter((entry): entry is ListEntry & { id: string } => Boolean(entry.id))
      .map((entry) => [entry.id, entry]),
  );

  const mergedEntries = templateEntries.map((templateEntry) => {
    const entryId = templateEntry.id ?? '';
    const locked = isEntryLocked(templateModule, entryId);
    const sourceEntry = entryId ? sourceById.get(entryId) : undefined;

    if (publishRollout) {
      return locked ? templateEntry : (sourceEntry ?? templateEntry);
    }

    if (resyncOnly) {
      return locked ? templateEntry : (sourceEntry ?? templateEntry);
    }

    if (sourceEntry && !locked) {
      return { ...templateEntry, ...sourceEntry, id: entryId };
    }
    return templateEntry;
  });

  return {
    ...templateConfig,
    [listKey]: mergedEntries,
    ...(templateConfig.layout !== undefined || sourceConfig.layout !== undefined
      ? { layout: templateConfig.layout ?? sourceConfig.layout }
      : {}),
  };
}

export function mergeModuleConfig(
  templateModule: GovernedModule,
  sourceModule: GovernedModule,
  options: MergeModuleConfigOptions,
): Record<string, unknown> {
  const listKey = getModuleListConfigKey(templateModule.type);
  if (listKey) {
    return mergeListModuleConfig(
      templateModule,
      sourceModule,
      listKey,
      options,
    );
  }

  if (templateModule.type === 'team_members') {
    const locked = isEntryLocked(templateModule, MODULE_ENTRY_WHOLE_MODULE_KEY);
    if (
      options.publishRollout &&
      options.preserveUngovernedOnPublish &&
      !locked
    ) {
      return { ...(sourceModule.config ?? {}) };
    }
    const base = locked
      ? { ...(templateModule.config ?? {}) }
      : { ...(sourceModule.config ?? {}) };
    return { ...base, members: [] };
  }

  if (options.publishRollout) {
    const lockedWholeModule = isEntryLocked(
      templateModule,
      MODULE_ENTRY_WHOLE_MODULE_KEY,
    );
    if (options.preserveUngovernedOnPublish && !lockedWholeModule) {
      return { ...(sourceModule.config ?? {}) };
    }
    return { ...(templateModule.config ?? {}) };
  }

  if (isEntryLocked(templateModule, MODULE_ENTRY_WHOLE_MODULE_KEY)) {
    return { ...(templateModule.config ?? {}) };
  }

  return { ...(sourceModule.config ?? {}) };
}
